// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useContext, useState, useEffect, useRef } from 'react'
import './Navbar.scss'
import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from "react-i18next";

// Firebase imports
import { signOut } from 'firebase/auth';
import { auth, firestore } from '../../context/FirebaseConfig';
import { doc, setDoc, getDocs, collection, updateDoc} from 'firebase/firestore'
import { ChevronDown, Factory, Languages, LogOut, User } from 'lucide-react';

export default function Navbar({ title }) {
    const { i18n, t } = useTranslation();
    const { currentUser, changeActiveDevice } = useContext(AuthContext);
    const [ clients, setClients ] = useState({});
    const [ dropdownOpen, setDropdownOpen ] = useState(false);
    const userDropdownRef = useRef(null);

    async function getClients() {
        const snapshots = await getDocs(collection(firestore, "clients"))
        const clientsNew = {};
        snapshots.forEach((doc) => {
            clientsNew[doc.id] = doc.data();
        });
        setClients(clientsNew);
    }

    React.useEffect(() => {
        if (currentUser.info.user_flags && currentUser.info.user_flags.includes('admin')) {
            getClients();
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }

            // Close any open dropdowns when clicking elsewhere
            const isDropdownClick = event.target.closest('.navbar--selector');
            if (!isDropdownClick) {
                document.querySelectorAll('.dropdown-menu').forEach(menu => {
                    menu.style.display = 'none';
                });
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle language change
    const handleLanguageChange = async (e) => {
        const selectedLanguage = e.target.value;
        i18n.changeLanguage(selectedLanguage);
        // Update the language code for the user
        await setLanguageCode(currentUser, selectedLanguage);
        setDropdownOpen(false); // Close dropdown after selection
    };

    const setLanguageCode = async (user, languageCode) => {
        if (user) {
            const userRef = doc(firestore, 'users', user.uid);
            await setDoc(userRef, { languageCode: languageCode }, { merge: true });
        }
    };

    async function handleClientChange(client) {
        await updateDoc(doc(firestore, "users", currentUser.uid), {"company": client});
        // Refresh the page to apply changes
        window.location.reload(false);
    }

    const handleSignOut = () => {
        signOut(auth);
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    return (
        <div className="navbar">
            <div className="navbar--left">
                <h1 className="navbar--title">{title}</h1>
            </div>

            <div className="navbar--right">
                {/* Client selector for admins */}
                {currentUser.info.user_flags && currentUser.info.user_flags.includes('admin') && (
                    <div className="navbar--selector" onClick={(e) => e.stopPropagation()}>
                        <div className="navbar--dropdown-icon">
                            <Factory size={20}></Factory>
                        </div>
                        <div
                            className="navbar--dropdown navbar--dropdown-with-icon enhanced-select"
                            onClick={() => {
                                // Close any other open dropdowns
                                document.querySelectorAll('.dropdown-menu').forEach(menu => {
                                    if (menu.id !== 'admin-client-dropdown') {
                                        menu.style.display = 'none';
                                    }
                                });

                                const dropdown = document.getElementById('admin-client-dropdown');
                                if (dropdown) {
                                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                                }
                            }}
                        >
                            <span className="selected-value">
                                {clients[currentUser.company.company]?.company_displayname || 'Select Client'}
                            </span>
                        </div>
                        <div className="dropdown-menu" id="admin-client-dropdown" style={{display: 'none'}}>
                            {Object.keys(clients)
                                .sort((a, b) => clients[a].company_displayname.localeCompare(clients[b].company_displayname))
                                .map(id => (
                                    <div
                                        key={id}
                                        className={`dropdown-option ${currentUser.company.company === id ? 'selected' : ''}`}
                                        onClick={() => {
                                            handleClientChange(id);
                                            document.getElementById('admin-client-dropdown').style.display = 'none';
                                        }}
                                    >
                                        {clients[id].company_displayname}
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                {/* Company selector */}
                {Object.keys(currentUser.info.company_selection ?? {}).length > 1 && (
                    <div className="navbar--selector" onClick={(e) => e.stopPropagation()}>
                        <div className="navbar--dropdown-icon">
                            <Factory size={20}></Factory>
                        </div>
                        <div
                            className="navbar--dropdown navbar--dropdown-with-icon enhanced-select"
                            onClick={() => {
                                // Close any other open dropdowns
                                document.querySelectorAll('.dropdown-menu').forEach(menu => {
                                    if (menu.id !== 'company-dropdown') {
                                        menu.style.display = 'none';
                                    }
                                });

                                const dropdown = document.getElementById('company-dropdown');
                                if (dropdown) {
                                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                                }
                            }}
                        >
                            <span className="selected-value">
                                {currentUser.info.company_selection[currentUser.company.company] || 'Select Company'}
                            </span>
                        </div>
                        <div className="dropdown-menu" id="company-dropdown" style={{display: 'none'}}>
                            {Object.keys(currentUser.info.company_selection)
                                .sort((a, b) => currentUser.info.company_selection[a].localeCompare(currentUser.info.company_selection[b]))
                                .map(id => (
                                    <div
                                        key={id}
                                        className={`dropdown-option ${currentUser.company.company === id ? 'selected' : ''}`}
                                        onClick={() => {
                                            handleClientChange(id);
                                            document.getElementById('company-dropdown').style.display = 'none';
                                        }}
                                    >
                                        {currentUser.info.company_selection[id]}
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                {/* Device selector */}
                {currentUser?.devices && Object.keys(currentUser.devices).length > 1 && (
                    <div className="navbar--selector" onClick={(e) => e.stopPropagation()}>
                        <div className="navbar--dropdown-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect width="18" height="12" x="3" y="4" rx="2" ry="2"></rect>
                                <line x1="2" x2="22" y1="20" y2="20"></line>
                            </svg>
                        </div>
                        <div
                            className="navbar--dropdown navbar--dropdown-with-icon enhanced-select"
                            onClick={() => {
                                // Close any other open dropdowns
                                document.querySelectorAll('.dropdown-menu').forEach(menu => {
                                    if (menu.id !== 'device-dropdown') {
                                        menu.style.display = 'none';
                                    }
                                });

                                const dropdown = document.getElementById('device-dropdown');
                                if (dropdown) {
                                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                                }
                            }}
                        >
                            <span className="selected-value">
                                {currentUser.devices[currentUser.device_id]?.display_name || currentUser.device_id || 'Select Device'}
                            </span>
                        </div>
                        <div className="dropdown-menu" id="device-dropdown" style={{display: 'none'}}>
                            {Object.keys(currentUser.devices)
                                .sort((a, b) => {
                                    const displayNameA = currentUser.devices[a]?.display_name ?? a;
                                    const displayNameB = currentUser.devices[b]?.display_name ?? b;
                                    return displayNameA.localeCompare(displayNameB);
                                })
                                .map(id => {
                        const displayName = currentUser.devices[id]?.display_name ?? id;
                                    return (
                                        <div
                                            key={id}
                                            className={`dropdown-option ${currentUser.device_id === id ? 'selected' : ''}`}
                                            onClick={() => {
                                                changeActiveDevice(id);
                                                document.getElementById('device-dropdown').style.display = 'none';
                                            }}
                                        >
                                            {displayName}
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                )}

                {/* User profile dropdown */}
                <div className="navbar--user-dropdown" ref={userDropdownRef}>
                    <button
                        className="navbar--user-button"
                        onClick={toggleDropdown}
                        aria-expanded={dropdownOpen}
                        aria-label="User menu"
                    >
                        {currentUser.info.photoURL ? (
                            <img
                                src={currentUser.info.photoURL}
                                alt={currentUser.info.displayName}
                                className="navbar--user-avatar"
                            />
                        ) : (
                            <div className="navbar--user-avatar navbar--user-avatar-placeholder">
                                <User></User>
                            </div>
                        )}
                        <span className="navbar--username">{currentUser.info.displayName}</span>
                        <ChevronDown size={18}></ChevronDown>
                    </button>

                    {dropdownOpen && (
                        <div className="navbar--dropdown-menu">
                            <div className="navbar--dropdown-header">
                                {currentUser.info.photoURL ? (
                                    <img
                                        src={currentUser.info.photoURL}
                                        alt={currentUser.info.displayName}
                                        className="navbar--dropdown-avatar"
                                    />
                                ) : (
                                    <div className="navbar--dropdown-avatar navbar--user-avatar-placeholder">
                                        <User size={24}></User>
                                    </div>
                                )}
                                <div className="navbar--dropdown-user-info">
                                    <span className="navbar--dropdown-name">{currentUser.info.displayName}</span>
                                    <span className="navbar--dropdown-email">{currentUser.info.email}</span>
                                </div>
                            </div>

                            <div className="navbar--dropdown-divider"></div>

                            {/* Language selector moved inside dropdown */}
                            <button
                                className="navbar--dropdown-item navbar--language-selector"
                                onClick={() => {
                                    // Toggle a language - simple switch between en and de
                                    const newLang = i18n.language === 'en' ? 'de' : 'en';
                                    i18n.changeLanguage(newLang);
                                    setLanguageCode(currentUser, newLang);
                                    setDropdownOpen(false);
                                }}
                            >
                                <Languages size={20}></Languages>
                                <span>{t("navbar_language")}</span>
                                <div className="navbar--language-toggle">
                                    <span
                                        className={`toggle-option ${i18n.language === 'en' ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (i18n.language !== 'en') {
                                                i18n.changeLanguage('en');
                                                setLanguageCode(currentUser, 'en');
                                                setDropdownOpen(false);
                                            }
                                        }}
                                    >
                                        EN
                                    </span>
                                    <span
                                        className={`toggle-option ${i18n.language === 'de' ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (i18n.language !== 'de') {
                                                i18n.changeLanguage('de');
                                                setLanguageCode(currentUser, 'de');
                                                setDropdownOpen(false);
                                            }
                                        }}
                                    >
                                        DE
                                    </span>
                                </div>
                            </button>

                            <div className="navbar--dropdown-divider"></div>

                            <button className="navbar--dropdown-item" onClick={handleSignOut}>
                                <LogOut size={18}></LogOut>
                                <span>{t("navbar_logout")}</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Hidden logout button for backward compatibility */}
                <button onClick={handleSignOut} style={{display:"none"}} id="logout_icon"/>
            </div>
        </div>
    );
}